import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { VendorService } from "./VendorService";
import { resolveLandingPathByModule } from "../layouts/SideBar/SideMenu";
import {
  fetchPermissionsAndUpdateUser,
  resolveSessionRoleName,
} from "../components/helper/utility";
import { parseUserPlantNumbers } from "../utils/listScope";

const AUTH_API =
  process.env.REACT_APP_API_ENDPOINT +
  process.env.REACT_APP_AUTHORIZATION_SERVICE_PORT;

const USER_API =
  process.env.REACT_APP_API_ENDPOINT + process.env.REACT_APP_USER_SERVICE_PORT;

/** Dispatched after localStorage user is cleared so Root can flip to login routes without a full reload. */
export const VENDOR_PORTAL_LOGOUT_EVENT = "vendor-portal-logout";

/** Dispatched after localStorage user is refreshed (e.g. default role change on Profile). */
export const VENDOR_PORTAL_USER_UPDATED_EVENT = "vendor-portal-user-updated";

const currentUser = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** Active session role from the header switcher (name, else selected role id). */
const getAcceptRole = () => {
  const user = currentUser();
  if (!user) return "";
  const role = user.role;
  if (typeof role === "string" && role.trim()) return role.trim();
  if (role && typeof role === "object") {
    const name = role.name || role.roleName || role.role || role.label;
    if (name != null && String(name).trim()) return String(name).trim();
  }
  const roleId = user.roleId;
  if (roleId == null || roleId === "") return "";
  const s = String(roleId).trim();
  return s.includes(",") ? s.split(",")[0].trim() : s;
};

const isUserLoggedIn = () => {
  return Boolean(localStorage.getItem("user"));
};

const clearLocalSession = () => {
  try {
    if (localStorage.getItem("user")) {
      localStorage.removeItem("user");
    }
  } catch {
    localStorage.removeItem("user");
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(VENDOR_PORTAL_LOGOUT_EVENT));
  }
};

let logoutInFlight = null;

/** GET authorization/api/auth/logout with the current JWT, then clear the local session. */
const logout = async () => {
  if (logoutInFlight) return logoutInFlight;

  logoutInFlight = (async () => {
    const token = currentUser()?.token;
    try {
      if (token) {
        await axios.get(`${AUTH_API}auth/logout`, {
          // Avoid the 401 interceptor re-entering logout while this request is in flight.
          skipAuth: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      }
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      clearLocalSession();
      logoutInFlight = null;
    }
  })();

  return logoutInFlight;
};

/** Persist a minimal session so getApiHeader() can authorize follow-up calls. */
const seedSessionToken = (token, userId) => {
  const seed = { token };
  if (userId != null && userId !== "") {
    seed.id = userId;
  }
  localStorage.setItem("user", JSON.stringify(seed));
};

const resolveUserIdFromLogin = (loginData) => {
  if (!loginData || typeof loginData !== "object") return null;
  if (loginData.id != null && loginData.id !== "") return loginData.id;
  if (loginData.userId != null && loginData.userId !== "") {
    return loginData.userId;
  }
  try {
    if (loginData.token) {
      const claims = jwtDecode(loginData.token);
      const fromJwt =
        claims?.UserId ??
        claims?.userId ??
        claims?.id ??
        claims?.sub ??
        null;
      if (fromJwt != null && fromJwt !== "") return fromJwt;
    }
  } catch {
    /* ignore */
  }
  return null;
};

/**
 * Load the latest user row via listUsers filtered by id
 * (same API used by Users / UserFormPage).
 */
const fetchUserProfileById = async (userId) => {
  if (userId == null || userId === "") return null;
  const response = await VendorService.getUsers({
    page: 0,
    size: 1,
    userId,
  });
  const rows = Array.isArray(response?.result) ? response.result : [];
  if (!rows.length) return null;
  return (
    rows.find((row) => String(row?.id) === String(userId)) || rows[0] || null
  );
};

const resolveLandingModule = async (roleId) => {
  if (roleId == null || roleId === "") return null;
  try {
    const roleResponse = await VendorService.getRoles({
      id: roleId,
      page: 0,
      size: 1,
    });
    const landingCode = roleResponse?.result?.[0]?.landingModule;
    if (landingCode) {
      return resolveLandingPathByModule(landingCode);
    }
  } catch (roleError) {
    console.error("Error loading role:", roleError);
  }
  return null;
};

/**
 * Build the stored session user object:
 * - profile fields from listUsers
 * - token (and optional session overrides) preserved from auth/session
 */
const buildSessionUser = ({
  profile,
  token,
  landingModule,
  sessionOverrides = {},
}) => {
  const profileId =
    profile?.id ?? profile?.userId ?? profile?.user_id ?? sessionOverrides.id;
  const plants = parseUserPlantNumbers(profile?.plants ?? profile?.plant);
  return {
    ...profile,
    ...sessionOverrides,
    token,
    id: profileId,
    plants,
    ...(landingModule != null ? { landingModule } : {}),
  };
};

/**
 * Refresh stored user from listUsers by id, then reload permissions.
 * Keeps token + in-session role selection (Profile default-role switch).
 */
const refreshSessionUser = async () => {
  const existing = currentUser();
  if (!existing?.token) return null;

  const userId = existing.id ?? existing.userId;
  let profile = null;

  if (userId != null && userId !== "") {
    try {
      profile = await fetchUserProfileById(userId);
    } catch (error) {
      console.error("Failed to refresh user profile:", error);
    }
  }

  let userData = existing;
  if (profile) {
    userData = buildSessionUser({
      profile,
      token: existing.token,
      landingModule: existing.landingModule,
      sessionOverrides: {
        // Preserve Profile "active role" local switch (not persisted to API)
        roleId: existing.roleId ?? profile.roleId,
        role: existing.role ?? profile.role,
        multiRoleIds: existing.multiRoleIds ?? profile.multiRoleIds,
      },
    });
  }

  userData = await resolveSessionRoleName(userData);
  return fetchPermissionsAndUpdateUser(userData);
};

const login = async (data) => {
  try {
    const customConfig = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    const response = await axios.post(
      `${AUTH_API}auth/login`,
      data,
      customConfig
    );

    if (!response.data?.token) {
      return response.data;
    }

    const token = response.data.token;
    const userId = resolveUserIdFromLogin(response.data);

    // Token (+ id) only from login — required before authenticated user APIs
    seedSessionToken(token, userId);

    let profile = null;
    if (userId != null && userId !== "") {
      try {
        profile = await fetchUserProfileById(userId);
      } catch (error) {
        console.error("Failed to load user profile after login:", error);
      }
    }

    // Prefer listUsers profile; fall back to login payload so login never breaks
    const profileSource = profile || response.data;
    const roleId = profileSource?.roleId ?? response.data.roleId;
    const landingModule = await resolveLandingModule(roleId);

    let userData = buildSessionUser({
      profile: profileSource,
      token,
      landingModule,
      sessionOverrides: {
        id: profileSource?.id ?? profileSource?.userId ?? userId,
      },
    });

    userData = await resolveSessionRoleName(userData);
    userData = await fetchPermissionsAndUpdateUser(userData);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event(VENDOR_PORTAL_USER_UPDATED_EVENT));
    }
    return userData;
  } catch (error) {
    console.error("Login error:", error);
    throw error;
  }
};

const authOpenHeader = () => ({
  headers: {
    "Content-Type": "application/json",
  },
});

const extractAuthMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data?.message) return String(data.message);
  if (data?.error) return String(data.error);
  if (error?.message) return String(error.message);
  return fallback;
};

/** POST user-service /auth/forgot-password/otp — { email } */
const requestForgotPasswordOtp = async (email) => {
  try {
    const response = await axios.post(
      `${USER_API}auth/forgot-password/otp`,
      { email: String(email || "").trim() },
      authOpenHeader()
    );
    return response.data;
  } catch (error) {
    const err = new Error(
      extractAuthMessage(error, "Could not send OTP. Please try again.")
    );
    err.response = error?.response;
    err.status = error?.response?.status;
    throw err;
  }
};

/** POST user-service /auth/forgot-password/reset — { email, otp, newPassword } */
const resetForgotPassword = async ({ email, otp, newPassword }) => {
  try {
    const response = await axios.post(
      `${USER_API}auth/forgot-password/reset`,
      {
        email: String(email || "").trim(),
        otp: String(otp || "").trim(),
        newPassword: String(newPassword || ""),
      },
      authOpenHeader()
    );
    return response.data;
  } catch (error) {
    const err = new Error(
      extractAuthMessage(error, "Could not reset password. Please try again.")
    );
    err.response = error?.response;
    err.status = error?.response?.status;
    throw err;
  }
};

export const AuthService = {
  login,
  logout,
  currentUser,
  getAcceptRole,
  isUserLoggedIn,
  refreshSessionUser,
  fetchUserProfileById,
  requestForgotPasswordOtp,
  resetForgotPassword,
};
